import * as THREE from 'three'
import { Perlin } from '../core/noise'
import { Grad } from './cities'
import { Heightmap, smoothstep } from './heightmap'
import {
  GRADOVI, LAT_SREDINA, LON_SKRACENJE, LON_SREDINA, MORA, OKVIR, OSTRVA,
  REKE, VENCI, VEZE, VISORAVNI, doLinije, doObrisa, uPoligonu,
} from './geo'

/** Jedan put: temena po terenu i rasponi koji idu preko vode kao most. */
export interface Putanja {
  od: string
  do: string
  tacke: THREE.Vector3[]
  /** Indeksi u `tacke`: [pocetak, kraj] svakog mosta. */
  mostovi: [number, number][]
}

export interface Evropa {
  hm: Heightmap
  gradovi: Grad[]
  putanje: Putanja[]
}

/** Rezolucija na kojoj se računaju poligoni; posle se interpolira na punu. */
const RES_GRUBO = 256

const VISINA_NIZIJE = 30
const DUBINA_MORA = 62
/** Dubina korita reke ispod nivoa mora — koliko se plavo vidi. */
const KORITO = 5

export function generisiEvropu(opts: { size: number; seed: number; res: number }): Evropa {
  const { size, res, seed } = opts
  const grubo = rasterizujGrubo(size, seed)
  const heights = naPunuRezoluciju(grubo, res, seed)

  const hm = new Heightmap({ size, res, seed, vrsta: 'evropa', heights })
  hm.biom = biomPoSirini(size, res, seed)

  const gradovi = postaviGradove(hm, size)
  const putanje = nadjiPuteve(hm, gradovi)
  poravnajTerenPodPutevima(hm, putanje)
  hm.preracunaj()

  // Posle poravnanja teren je drugačiji, pa temena puteva liježu ponovo —
  // i tek onda se zna gde stvarno treba most.
  for (const p of putanje) {
    for (const t of p.tacke) t.y = hm.sample(t.x, t.z)
    p.mostovi = nadjiMostove(p.tacke)
  }
  for (const g of gradovi) {
    g.pozicija.y = Math.max(0.5, hm.sample(g.pozicija.x, g.pozicija.z))
  }

  if (import.meta.env.DEV) {
    const mostova = putanje.reduce((z, p) => z + p.mostovi.length, 0)
    console.log(`[evropa] gradova ${gradovi.length}, puteva ${putanje.length}, mostova ${mostova}`)
  }

  return { hm, gradovi, putanje }
}

// --- pretvaranje stepeni u svetske koordinate -------------------------------

function skala(size: number): number {
  return size / (OKVIR.latMax - OKVIR.latMin)
}

export function uSvet(lon: number, lat: number, size: number): { x: number; z: number } {
  const s = skala(size)
  return {
    x: (lon - LON_SREDINA) * s * LON_SKRACENJE,
    z: -(lat - LAT_SREDINA) * s,
  }
}

function uStepene(x: number, z: number, size: number): { lon: number; lat: number } {
  const s = skala(size)
  return {
    lon: LON_SREDINA + x / (s * LON_SKRACENJE),
    lat: LAT_SREDINA - z / s,
  }
}

// --- rasterizacija ---------------------------------------------------------

/**
 * Grubo polje visina. Sve što traži prolazak kroz poligone računa se ovde,
 * na 257², jer je to nekoliko desetina miliona operacija — na punoj mreži
 * bi bilo nekoliko sekundi.
 */
function rasterizujGrubo(size: number, seed: number): Float32Array {
  const n = RES_GRUBO + 1
  const polje = new Float32Array(n * n)
  const sum = new Perlin(seed ^ 0x51ed270b)
  const grebenSum = new Perlin(seed ^ 0x1a2b3c4d)

  const korak = size / RES_GRUBO
  const pola = size / 2

  for (let j = 0; j < n; j++) {
    const z = -pola + j * korak
    for (let i = 0; i < n; i++) {
      const x = -pola + i * korak
      const { lon, lat } = uStepene(x, z, size)

      // Obali treba dosta šuma: poligoni su zapisani sa malo tačaka, pa bi
      // bez ovoga obala bila vidljivo sastavljena od dugih pravih poteza.
      const talas =
        sum.fbm(lon * 0.85, lat * 0.85, 5) * 0.3 + sum.fbm(lon * 3.1, lat * 3.1, 3) * 0.09

      let uMoru = false
      let doObale = Infinity
      for (const m of MORA) {
        const d = doObrisa(m, lon, lat)
        if (d < doObale) doObale = d
        if (uPoligonu(m, lon, lat)) uMoru = true
      }
      let naOstrvu = false
      for (const o of OSTRVA) {
        const d = doObrisa(o, lon, lat)
        if (d < doObale) doObale = d
        if (uPoligonu(o, lon, lat)) naOstrvu = true
      }
      const kopno = naOstrvu || !uMoru
      // Predznak: pozitivno u kopnu, negativno u moru.
      const rastojanje = (kopno ? doObale : -doObale) + talas

      let y: number
      if (rastojanje <= 0) {
        // More: uzak šelf pa brzo u dubinu, da obala ostane čitka linija.
        y = -DUBINA_MORA * smoothstep(0, 1.3, -rastojanje)
      } else {
        // Kopno se diže brzo od obale. Blaga rampa je delovala prirodno,
        // ali daje pojas peska širok kao pola Francuske.
        y = VISINA_NIZIJE * smoothstep(0, 0.5, rastojanje)
        y += sum.fbm(lon * 1.5 + 40, lat * 1.5 - 10, 5) * 14
      }

      if (kopno) {
        for (const v of VISORAVNI) {
          const dx = (lon - v.lon) / v.rx
          const dz = (lat - v.lat) / v.rz
          const t = Math.sqrt(dx * dx + dz * dz)
          y += v.visina * smoothstep(1.15, 0.25, t)
        }
        for (const p of VENCI) {
          const d = doLinije(p.osa, lon, lat)
          if (d > p.sirina * 2.2) continue
          const jezgro = smoothstep(p.sirina * 1.9, 0, d)
          // Greben nije glatka grba — ridged šum mu daje vrhove i prevoje.
          // Frekvencija prati širinu venca: široki venci sa gustim šumom
          // ispadnu kao testera, a ne kao planina.
          const f = 1.9 / Math.max(0.5, p.sirina)
          const kvrga = 0.62 + 0.38 * grebenSum.ridged(lon * f, lat * f, 4)
          y += p.visina * Math.pow(jezgro, 1.4) * kvrga
        }
      }

      // Ivice mape idu pod vodu. Bez toga se teren završava zidom u vazduhu,
      // a i mapa dobija okvir umesto da se Rusija preseče na pola.
      const rub = Math.max(Math.abs(x), Math.abs(z)) / (size / 2)
      const okvir = smoothstep(0.99, 0.88, rub)
      y = y * okvir - (1 - okvir) * 55

      polje[j * n + i] = y
    }
  }

  // Reke se urezuju posle planina, da mogu da proseku i venac.
  ureziReke(polje, size, n)
  return polje
}

/**
 * Reke. U nizijama se koriti spuštaju ispod nivoa mora, pa se vide kao voda;
 * više u planini ostaje samo dolina, jer bi kanal na 300 metara izgledao kao
 * greška, a mostovi ionako stoje dole gde su gradovi.
 */
function ureziReke(polje: Float32Array, size: number, n: number): void {
  const korak = size / RES_GRUBO
  const pola = size / 2
  // Poluširina korita i doline, u stepenima.
  const koritoSir = 0.16
  const dolinaSir = 0.55

  for (let j = 0; j < n; j++) {
    const z = -pola + j * korak
    for (let i = 0; i < n; i++) {
      const x = -pola + i * korak
      const k = j * n + i
      const okolina = polje[k]
      if (okolina < -8) continue

      const { lon, lat } = uStepene(x, z, size)
      let d = Infinity
      for (const r of REKE) {
        const dr = doLinije(r.tok, lon, lat)
        if (dr < d) d = dr
      }
      if (d > dolinaSir) continue

      // Dno je voda u niziji, a suva dolina u brdima.
      const dno = okolina < 55 ? -KORITO : Math.min(okolina - 26, 34)
      const dolina = smoothstep(dolinaSir, koritoSir, d)
      const korito = smoothstep(koritoSir, koritoSir * 0.45, d)
      let y = okolina + (dno - okolina) * (0.55 * dolina)
      y = y + (dno - y) * korito
      polje[k] = Math.min(polje[k], y)
    }
  }
}

/** Bilinearno na punu mrežu, plus sitan šum koji vrati teksturu terenu. */
function naPunuRezoluciju(grubo: Float32Array, res: number, seed: number): Float32Array {
  const n = res + 1
  const g = RES_GRUBO + 1
  const polje = new Float32Array(n * n)
  const detalj = new Perlin(seed ^ 0x7f4a7c15)
  const odnos = RES_GRUBO / res

  for (let j = 0; j < n; j++) {
    const gy = j * odnos
    const j0 = Math.min(g - 1, Math.floor(gy))
    const j1 = Math.min(g - 1, j0 + 1)
    const tz = gy - j0
    for (let i = 0; i < n; i++) {
      const gx = i * odnos
      const i0 = Math.min(g - 1, Math.floor(gx))
      const i1 = Math.min(g - 1, i0 + 1)
      const tx = gx - i0

      const h00 = grubo[j0 * g + i0]
      const h10 = grubo[j0 * g + i1]
      const h01 = grubo[j1 * g + i0]
      const h11 = grubo[j1 * g + i1]
      let y =
        h00 * (1 - tx) * (1 - tz) + h10 * tx * (1 - tz) + h01 * (1 - tx) * tz + h11 * tx * tz

      // Detalj samo na kopnu i srazmerno visini — obale i more ostaju mirni.
      if (y > 1) {
        const jacina = 3 + Math.min(18, y * 0.16)
        y += detalj.fbm((i / res) * 46, (j / res) * 46, 4) * jacina
      }
      polje[j * n + i] = y
    }
  }
  return polje
}

/**
 * Koliko je kraj suv, 0 = zeleno, 1 = sprženo. Ide po geografskoj širini,
 * jer bez toga cela Evropa ispadne u istoj nijansi zelene — Andaluzija i
 * Finska ne mogu da budu iste boje.
 */
function biomPoSirini(size: number, res: number, seed: number): Float32Array {
  const n = res + 1
  const polje = new Float32Array(n * n)
  const mrlje = new Perlin(seed ^ 0x3c6ef372)
  const korak = size / res
  const pola = size / 2
  for (let j = 0; j < n; j++) {
    const z = -pola + j * korak
    for (let i = 0; i < n; i++) {
      const x = -pola + i * korak
      const { lat } = uStepene(x, z, size)
      const suvo = smoothstep(48, 37, lat)
      polje[j * n + i] = Math.min(0.88, Math.max(0, suvo + mrlje.fbm(x * 0.006, z * 0.006, 4) * 0.28))
    }
  }
  return polje
}

// --- gradovi ---------------------------------------------------------------

/**
 * Gradovi idu na svoje stvarne koordinate, ali obala je ovde gruba, pa
 * primorski grad ume da završi u moru — tražimo najbližu suvu ćeliju.
 */
function postaviGradove(hm: Heightmap, size: number): Grad[] {
  const gradovi: Grad[] = []
  for (const g of GRADOVI) {
    const { x, z } = uSvet(g.lon, g.lat, size)
    const nadjeno = najblizeKopno(hm, x, z)
    if (!nadjeno) continue
    gradovi.push({
      ime: g.ime,
      pozicija: new THREE.Vector3(nadjeno.x, Math.max(0.5, hm.sample(nadjeno.x, nadjeno.z)), nadjeno.z),
    })
  }
  return gradovi
}

function najblizeKopno(hm: Heightmap, x: number, z: number): { x: number; z: number } | null {
  if (hm.sample(x, z) > 1) return { x, z }
  const korak = hm.cell * 2
  for (let prsten = 1; prsten <= 14; prsten++) {
    const r = prsten * korak
    const uzoraka = prsten * 8
    for (let a = 0; a < uzoraka; a++) {
      const ugao = (a / uzoraka) * Math.PI * 2
      const nx = x + Math.cos(ugao) * r
      const nz = z + Math.sin(ugao) * r
      if (hm.sample(nx, nz) > 1) return { x: nx, z: nz }
    }
  }
  return null
}

// --- putevi ----------------------------------------------------------------

/** Rezolucija mreže po kojoj se traži trasa. */
const RES_PUT = 168

function nadjiPuteve(hm: Heightmap, gradovi: Grad[]): Putanja[] {
  const poIme = new Map(gradovi.map((g) => [g.ime, g]))
  const mreza = pripremiMrezu(hm)
  const putanje: Putanja[] = []

  for (const [a, b] of VEZE) {
    const ga = poIme.get(a)
    const gb = poIme.get(b)
    if (!ga || !gb) continue
    const trasa = aStar(mreza, hm, ga.pozicija, gb.pozicija)
    if (!trasa) continue
    const tacke = zagladiTrasu(trasa, hm)
    putanje.push({ od: a, do: b, tacke, mostovi: nadjiMostove(tacke) })
  }
  return putanje
}

interface Mreza {
  res: number
  korak: number
  pola: number
  visine: Float32Array
}

function pripremiMrezu(hm: Heightmap): Mreza {
  const n = RES_PUT + 1
  const korak = hm.size / RES_PUT
  const pola = hm.size / 2
  const visine = new Float32Array(n * n)
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      visine[j * n + i] = hm.sample(-pola + i * korak, -pola + j * korak)
    }
  }
  return { res: RES_PUT, korak, pola, visine }
}

/** Cena prelaska u susednu ćeliju: uspon i voda su skupi, ali ne i zabranjeni. */
function cena(m: Mreza, od: number, na: number, duzina: number): number {
  const h0 = m.visine[od]
  const h1 = m.visine[na]
  const nagib = Math.abs(h1 - h0) / duzina
  let c = duzina * (1 + nagib * 26)
  // Preko vode se gradi most — dozvoljeno, ali mnogo skuplje od obilaska.
  // Sa manjom kaznom trasa je volela da "preseče" zaliv umesto da ide obalom.
  if (h1 < 0) c += duzina * 16
  // Visoke planine se zaobilaze i kad je nagib blag, ali ne po svaku cenu —
  // preko prevoja je i dalje bolje nego morem.
  if (h1 > 100) c += duzina * (h1 - 100) * 0.03
  return c
}

function aStar(
  m: Mreza,
  hm: Heightmap,
  od: THREE.Vector3,
  do_: THREE.Vector3,
): { x: number; z: number }[] | null {
  const n = m.res + 1
  const idx = (i: number, j: number): number => j * n + i
  const uMrezu = (x: number, z: number): [number, number] => [
    Math.max(0, Math.min(m.res, Math.round((x + m.pola) / m.korak))),
    Math.max(0, Math.min(m.res, Math.round((z + m.pola) / m.korak))),
  ]

  const [si, sj] = uMrezu(od.x, od.z)
  const [ci, cj] = uMrezu(do_.x, do_.z)
  const start = idx(si, sj)
  const cilj = idx(ci, cj)
  if (start === cilj) return null

  const g = new Float32Array(n * n).fill(Infinity)
  const f = new Float32Array(n * n).fill(Infinity)
  const odakle = new Int32Array(n * n).fill(-1)
  const zatvoren = new Uint8Array(n * n)

  const heuristika = (i: number, j: number): number =>
    Math.hypot(i - ci, j - cj) * m.korak

  g[start] = 0
  f[start] = heuristika(si, sj)

  // Binarni hip nad indeksima ćelija.
  const hip: number[] = [start]
  const gore = (k: number): void => {
    while (k > 0) {
      const r = (k - 1) >> 1
      if (f[hip[r]] <= f[hip[k]]) break
      ;[hip[r], hip[k]] = [hip[k], hip[r]]
      k = r
    }
  }
  const dole = (k: number): void => {
    for (;;) {
      const l = k * 2 + 1
      const d = l + 1
      let naj = k
      if (l < hip.length && f[hip[l]] < f[hip[naj]]) naj = l
      if (d < hip.length && f[hip[d]] < f[hip[naj]]) naj = d
      if (naj === k) break
      ;[hip[naj], hip[k]] = [hip[k], hip[naj]]
      k = naj
    }
  }

  const susedi = [
    [1, 0], [-1, 0], [0, 1], [0, -1],
    [1, 1], [1, -1], [-1, 1], [-1, -1],
  ]

  while (hip.length) {
    const tekuci = hip[0]
    hip[0] = hip[hip.length - 1]
    hip.pop()
    if (hip.length) dole(0)
    if (tekuci === cilj) break
    if (zatvoren[tekuci]) continue
    zatvoren[tekuci] = 1

    const ti = tekuci % n
    const tj = (tekuci - ti) / n
    for (const [di, dj] of susedi) {
      const ni = ti + di
      const nj = tj + dj
      if (ni < 0 || nj < 0 || ni > m.res || nj > m.res) continue
      const sused = idx(ni, nj)
      if (zatvoren[sused]) continue
      const duzina = Math.hypot(di, dj) * m.korak
      const novo = g[tekuci] + cena(m, tekuci, sused, duzina)
      if (novo >= g[sused]) continue
      g[sused] = novo
      f[sused] = novo + heuristika(ni, nj)
      odakle[sused] = tekuci
      hip.push(sused)
      gore(hip.length - 1)
    }
  }

  if (odakle[cilj] === -1) return null

  const trasa: { x: number; z: number }[] = []
  let k = cilj
  while (k !== -1) {
    const i = k % n
    const j = (k - i) / n
    trasa.push({ x: -m.pola + i * m.korak, z: -m.pola + j * m.korak })
    k = odakle[k]
  }
  trasa.reverse()
  // Krajevi tačno na gradovima, ne na centru ćelije.
  trasa[0] = { x: od.x, z: od.z }
  trasa[trasa.length - 1] = { x: do_.x, z: do_.z }
  void hm
  return trasa
}

/** Chaikin zaobljavanje — trasa iz A* je stepenasta, put mora da vijuga meko. */
function zagladiTrasu(trasa: { x: number; z: number }[], hm: Heightmap): THREE.Vector3[] {
  let tacke = trasa
  for (let prolaz = 0; prolaz < 3; prolaz++) {
    const nove: { x: number; z: number }[] = [tacke[0]]
    for (let i = 0; i < tacke.length - 1; i++) {
      const a = tacke[i]
      const b = tacke[i + 1]
      nove.push({ x: a.x * 0.75 + b.x * 0.25, z: a.z * 0.75 + b.z * 0.25 })
      nove.push({ x: a.x * 0.25 + b.x * 0.75, z: a.z * 0.25 + b.z * 0.75 })
    }
    nove.push(tacke[tacke.length - 1])
    tacke = nove
  }

  // Prorediti na ujednačen korak, da traka puta ima pravilne segmente.
  const cilj = hm.size / 220
  const rezultat: THREE.Vector3[] = []
  let preostalo = 0
  for (let i = 0; i < tacke.length - 1; i++) {
    const a = tacke[i]
    const b = tacke[i + 1]
    const d = Math.hypot(b.x - a.x, b.z - a.z)
    if (d < 1e-6) continue
    let t = preostalo
    while (t < d) {
      const u = t / d
      const x = a.x + (b.x - a.x) * u
      const z = a.z + (b.z - a.z) * u
      rezultat.push(new THREE.Vector3(x, hm.sample(x, z), z))
      t += cilj
    }
    preostalo = t - d
  }
  const kraj = tacke[tacke.length - 1]
  rezultat.push(new THREE.Vector3(kraj.x, hm.sample(kraj.x, kraj.z), kraj.z))
  return rezultat
}

/**
 * Potopljeni delovi trase postaju mostovi. Nije dovoljno uzeti samo tačke
 * ispod nule: kod obale visina skače oko nule, pa bi jedan prelaz ispao kao
 * pet odvojenih ploča koje lebde nad vodom. Zato se pojas proširi na obe
 * strane i bliski rasponi se spoje u jedan most.
 */
function nadjiMostove(tacke: THREE.Vector3[]): [number, number][] {
  const n = tacke.length
  const nadVodom = new Uint8Array(n)
  for (let i = 0; i < n; i++) if (tacke[i].y < 1.2) nadVodom[i] = 1

  // Proširenje: most mora da stigne do suvog na obe obale, ali samo preko
  // niskog priobalja — inače bi se povukao duboko u kopno i grad na kraju
  // trase bi završio na mostu.
  const prosireno = new Uint8Array(nadVodom)
  for (let i = 0; i < n; i++) {
    if (!nadVodom[i]) continue
    for (const smer of [-1, 1]) {
      for (let d = 1; d <= 2; d++) {
        const k = i + smer * d
        if (k < 0 || k >= n) break
        if (tacke[k].y > 5) break
        prosireno[k] = 1
      }
    }
  }

  const sirovi: [number, number][] = []
  let pocetak = -1
  for (let i = 0; i < n; i++) {
    if (prosireno[i] && pocetak === -1) pocetak = i
    if (!prosireno[i] && pocetak !== -1) {
      sirovi.push([pocetak, i - 1])
      pocetak = -1
    }
  }
  if (pocetak !== -1) sirovi.push([pocetak, n - 1])

  const mostovi: [number, number][] = []
  for (const raspon of sirovi) {
    const prethodni = mostovi[mostovi.length - 1]
    // Dva raspona razdvojena sa par tačaka kopna su i dalje jedan most.
    if (prethodni && raspon[0] - prethodni[1] <= 5) prethodni[1] = raspon[1]
    else mostovi.push([raspon[0], raspon[1]])
  }
  return mostovi.filter(([a, b]) => b - a >= 2)
}

/**
 * Teren se pod putem blago poravnava. Bez toga traka puta lebdi ili propada
 * kroz sitne izbočine, a i deluje kao da je put stvarno usečen.
 */
function poravnajTerenPodPutevima(hm: Heightmap, putanje: Putanja[]): void {
  const n = hm.res + 1
  const pola = hm.size / 2
  const pojas = hm.size * 0.008
  const pojas2 = pojas * pojas

  for (const put of putanje) {
    // Uzdužni profil se zagladi, ali samo preko kopna. Ako se u prosek uzme
    // i deo trase nad morem, ceo profil se povuče ka nuli i put onda useca
    // grad u kome počinje — Pariz je tako završavao na nivou mora.
    const profil = put.tacke.map((t) => t.y)
    const naKopnu = put.tacke.map((t) => t.y > 1.5)
    for (let p = 0; p < 6; p++) {
      for (let i = 1; i < profil.length - 1; i++) {
        if (!naKopnu[i] || !naKopnu[i - 1] || !naKopnu[i + 1]) continue
        profil[i] = (profil[i - 1] + profil[i] * 2 + profil[i + 1]) * 0.25
      }
    }

    for (let k = 0; k < put.tacke.length; k++) {
      const t = put.tacke[k]
      const ciljna = profil[k]
      // Ni voda ni pojas uz obalu se ne diraju — tu ide most, ne nasip.
      if (!naKopnu[k] || ciljna < 4) continue

      const i0 = Math.max(0, Math.floor((t.x - pojas + pola) / hm.cell))
      const i1 = Math.min(n - 1, Math.ceil((t.x + pojas + pola) / hm.cell))
      const j0 = Math.max(0, Math.floor((t.z - pojas + pola) / hm.cell))
      const j1 = Math.min(n - 1, Math.ceil((t.z + pojas + pola) / hm.cell))

      for (let j = j0; j <= j1; j++) {
        const z = -pola + j * hm.cell
        for (let i = i0; i <= i1; i++) {
          const x = -pola + i * hm.cell
          const d2 = (x - t.x) ** 2 + (z - t.z) ** 2
          if (d2 > pojas2) continue
          const w = smoothstep(1, 0.15, Math.sqrt(d2) / pojas) * 0.75
          const idx = j * n + i
          if (hm.heights[idx] < 0.5) continue
          hm.heights[idx] += (ciljna - hm.heights[idx]) * w
        }
      }
    }
  }
}
